"use client";

import * as React from "react";
import { ChevronDown } from "lucide-react";
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
} from "@aio/platform-ui";
import type { ColumnDef } from "../columns";
import {
  getOperatorById,
  getOperatorsForColumn,
  type ColumnWithCustomOperators,
  type OperatorDef,
  type OperatorId,
} from "./operators";

/**
 * The shape of a single in-progress / active filter row.
 *
 * - `columnKey` matches `ColumnDef.key`.
 * - `operatorId` is the operator's stable id.
 * - `value` adapts to the operator's `valueShape`:
 *   - `single` → string (or whatever scalar the column understands)
 *   - `range` → `[low, high]` tuple
 *   - `multi` → string[] (comma-separated tokens for the fallback Input)
 *   - `none` (operators with `requiresValue: false`) → null
 *
 * `value` is intentionally `unknown`-ish here so the type doesn't fight the
 * UI when a user is mid-edit. The eventual normaliser (Phase G's data-table)
 * coerces values into typed predicates per column type.
 */
export interface ActiveFilter {
  /** Column key this filter applies to. */
  columnKey: string;
  /** Operator id (e.g. "contains", "between", "isEmpty"). */
  operatorId: OperatorId;
  /**
   * Filter value. Shape depends on the operator's `valueShape`:
   *  - "single" → string
   *  - "range"  → [string, string]
   *  - "multi"  → string[]
   * Use `null` for valueless operators (`isEmpty`, `today`, etc).
   */
  value: string | [string, string] | string[] | null;
}

/**
 * Props for {@link FilterInput}.
 */
export interface FilterInputProps<TRow> {
  /**
   * The full column list. The component itself filters out non-`filterable`
   * columns and respects each column's declared type when picking operators.
   */
  columns: Array<ColumnDef<TRow> & ColumnWithCustomOperators>;
  /** The in-progress filter draft. `null` means "no column picked yet". */
  value: ActiveFilter | null;
  /**
   * Notified whenever the draft changes (column / operator / value). The
   * caller is the source of truth so this component is fully controllable.
   */
  onChange: (next: ActiveFilter) => void;
  /**
   * Fired when the user clicks "Apply". The handler typically pushes the
   * filter onto the FilterBar's chip list and resets `value` to `null`.
   */
  onSubmit: (filter: ActiveFilter) => void;
  /** Optional override for the Apply button label. Defaults to "Apply". */
  applyLabel?: string;
  /** Extra classes for the outer wrapper. */
  className?: string;
}

/**
 * Read the visible label from a column. Mirrors the convention used elsewhere
 * in the engine — `ColumnDef.header` may be a literal string OR an i18n key,
 * and the table shell handles resolution; this component renders strings as-is.
 */
function readColumnLabel<TRow>(column: ColumnDef<TRow>): string {
  return column.header;
}

/**
 * Parse a value control's string into the right shape for the active filter.
 * Used by both the range and multi inputs. Splits multi values on commas,
 * trims whitespace, and drops empties so a trailing comma doesn't carry an
 * empty token through to consumers.
 */
function parseMultiInput(raw: string): string[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Find the column matching the active filter, or `undefined` when none.
 * Returning `undefined` is meaningful — it lets the value control hide.
 */
function findColumn<TRow>(
  columns: Array<ColumnDef<TRow> & ColumnWithCustomOperators>,
  key: string | undefined,
): (ColumnDef<TRow> & ColumnWithCustomOperators) | undefined {
  if (!key) return undefined;
  return columns.find((c) => c.key === key);
}

/**
 * Compute the default operator for a column. Picks the first operator in the
 * column's operator list — text columns get `contains`, numeric get `equals`,
 * date columns get `on`, categorical get `equals`, and custom columns return
 * the first caller-supplied operator (or undefined when the column has none).
 */
function pickDefaultOperator<TRow>(
  column: ColumnDef<TRow> & ColumnWithCustomOperators,
): OperatorDef | undefined {
  const list = getOperatorsForColumn(column);
  return list[0];
}

/**
 * Build the `value` field that goes with a given operator's value shape. We
 * pick neutral defaults so the value control renders without flicker.
 */
function emptyValueFor(operator: OperatorDef): ActiveFilter["value"] {
  if (!operator.requiresValue) return null;
  switch (operator.valueShape) {
    case "single":
      return "";
    case "range":
      return ["", ""];
    case "multi":
      return [];
  }
}

/**
 * Determine whether the Apply button should be enabled for the given draft.
 *
 * Rules:
 * - No column → disabled.
 * - No operator → disabled.
 * - Operator with requiresValue=false → enabled.
 * - Single → enabled when value is a non-empty string.
 * - Range → enabled when at least one of low/high is non-empty (the consumer
 *   normaliser may decide what an open-ended range means; we don't gate that).
 * - Multi → enabled when at least one token is present.
 */
function canApply(
  filter: ActiveFilter | null,
  operator: OperatorDef | undefined,
): boolean {
  if (!filter || !operator) return false;
  if (!operator.requiresValue) return true;
  const v = filter.value;
  switch (operator.valueShape) {
    case "single":
      return typeof v === "string" && v.length > 0;
    case "range":
      return Array.isArray(v) && v.length === 2 && (v[0]!.length > 0 || v[1]!.length > 0);
    case "multi":
      return Array.isArray(v) && v.length > 0;
  }
}

/**
 * One filter row. Layout (left → right):
 *
 *   [ Column dropdown ] [ Operator dropdown ] [ Value control ] [ Apply ]
 *
 * The component is fully controlled: it accepts a `value` prop describing
 * the in-progress draft and a single `onChange` callback. `onSubmit` is
 * called when the user clicks Apply (typically the FilterBar will push the
 * resulting filter into its chip list and clear the draft).
 *
 * The value control adapts to the chosen operator's `valueShape`:
 * - `single` → one Input
 * - `range`  → two Inputs side by side (low / high)
 * - `multi`  → one comma-separated Input fallback (Combobox primitive
 *              isn't shipping yet; comma-split is the documented stand-in)
 *
 * The component does NOT dispatch the filter against a dataset — its only
 * responsibility is producing well-formed {@link ActiveFilter} drafts. The
 * data-table composer applies the resulting predicates to TanStack Table.
 */
export function FilterInput<TRow>({
  columns,
  value,
  onChange,
  onSubmit,
  applyLabel,
  className,
}: FilterInputProps<TRow>): React.JSX.Element {
  const filterableColumns = React.useMemo(
    () => columns.filter((c) => c.filterable !== false && c.filterable !== undefined),
    [columns],
  );
  // For columns that don't explicitly opt in, fall back to "filterable" when
  // any column has set the flag — otherwise treat all columns as filterable.
  // This mirrors the implicit-vs-explicit rule the rest of the engine uses
  // (sortable defaults to true unless the column says otherwise; we keep
  // filterable strict so columns like internal IDs stay out of the picker).
  // The branch above already only includes explicit `true` opt-ins; if the
  // caller has no filterable columns we fall back to the full list to avoid
  // rendering an empty picker.
  const visibleColumns =
    filterableColumns.length > 0 ? filterableColumns : columns;

  const activeColumn = findColumn(visibleColumns, value?.columnKey);
  const operatorList = activeColumn
    ? getOperatorsForColumn(activeColumn)
    : ([] as OperatorDef[]);
  const activeOperator = activeColumn && value
    ? getOperatorById(activeColumn.type, value.operatorId) ??
      operatorList.find((o) => o.id === value.operatorId)
    : undefined;

  const handlePickColumn = React.useCallback(
    (column: ColumnDef<TRow> & ColumnWithCustomOperators) => {
      const operator = pickDefaultOperator(column);
      if (!operator) {
        // Custom column without operators — emit a draft anyway so the UI can
        // surface "no operators configured". Use a sentinel id.
        onChange({
          columnKey: column.key,
          operatorId: "equals" as OperatorId,
          value: null,
        });
        return;
      }
      onChange({
        columnKey: column.key,
        operatorId: operator.id,
        value: emptyValueFor(operator),
      });
    },
    [onChange],
  );

  const handlePickOperator = React.useCallback(
    (operator: OperatorDef) => {
      if (!value) return;
      onChange({
        columnKey: value.columnKey,
        operatorId: operator.id,
        value: emptyValueFor(operator),
      });
    },
    [onChange, value],
  );

  const handleSingleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!value) return;
      onChange({ ...value, value: event.target.value });
    },
    [onChange, value],
  );

  const handleRangeChange = React.useCallback(
    (which: "low" | "high") =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!value) return;
        const current = Array.isArray(value.value)
          ? (value.value as [string, string])
          : ["", ""];
        const next: [string, string] =
          which === "low"
            ? [event.target.value, current[1] ?? ""]
            : [current[0] ?? "", event.target.value];
        onChange({ ...value, value: next });
      },
    [onChange, value],
  );

  const handleMultiChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!value) return;
      onChange({ ...value, value: parseMultiInput(event.target.value) });
    },
    [onChange, value],
  );

  const handleSubmit = React.useCallback(() => {
    if (!value) return;
    onSubmit(value);
  }, [onSubmit, value]);

  const applyDisabled = !canApply(value, activeOperator);

  return (
    <div
      className={cn("flex flex-col gap-3", className)}
      data-testid="filter-input"
      role="group"
      aria-label="Add filter"
    >
      <div className="flex flex-wrap items-end gap-2">
        {/* Column picker */}
        <div className="flex min-w-32 flex-col gap-1">
          <span className="text-xs font-medium text-fg-muted">Column</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="justify-between gap-2"
                data-testid="filter-input-column-trigger"
              >
                <span className="truncate">
                  {activeColumn ? readColumnLabel(activeColumn) : "Select…"}
                </span>
                <ChevronDown className="size-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-44">
              {visibleColumns.map((column) => (
                <DropdownMenuItem
                  key={column.key}
                  onSelect={() => handlePickColumn(column)}
                  data-testid={`filter-input-column-${column.key}`}
                >
                  {readColumnLabel(column)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Operator picker — only meaningful once a column is chosen */}
        <div className="flex min-w-32 flex-col gap-1">
          <span className="text-xs font-medium text-fg-muted">Operator</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={!activeColumn || operatorList.length === 0}
                className="justify-between gap-2"
                data-testid="filter-input-operator-trigger"
              >
                <span className="truncate">
                  {activeOperator?.label ?? "Select…"}
                </span>
                <ChevronDown className="size-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-44">
              {operatorList.map((operator) => (
                <DropdownMenuItem
                  key={operator.id}
                  onSelect={() => handlePickOperator(operator)}
                  data-testid={`filter-input-operator-${operator.id}`}
                >
                  {operator.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Value control — adapts to operator.valueShape */}
        {value && activeOperator?.requiresValue ? (
          <div className="flex min-w-40 flex-col gap-1">
            <span className="text-xs font-medium text-fg-muted">Value</span>
            {activeOperator.valueShape === "single" ? (
              <Input
                type="text"
                value={typeof value.value === "string" ? value.value : ""}
                onChange={handleSingleChange}
                placeholder="Value"
                className="h-9"
                data-testid="filter-input-value-single"
              />
            ) : null}
            {activeOperator.valueShape === "range" ? (
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={
                    Array.isArray(value.value) && value.value.length === 2
                      ? (value.value as [string, string])[0]
                      : ""
                  }
                  onChange={handleRangeChange("low")}
                  placeholder="Min"
                  className="h-9 w-24"
                  data-testid="filter-input-value-range-low"
                />
                <span className="text-sm text-fg-muted" aria-hidden="true">
                  to
                </span>
                <Input
                  type="text"
                  value={
                    Array.isArray(value.value) && value.value.length === 2
                      ? (value.value as [string, string])[1]
                      : ""
                  }
                  onChange={handleRangeChange("high")}
                  placeholder="Max"
                  className="h-9 w-24"
                  data-testid="filter-input-value-range-high"
                />
              </div>
            ) : null}
            {activeOperator.valueShape === "multi" ? (
              <Input
                type="text"
                value={
                  Array.isArray(value.value)
                    ? (value.value as string[]).join(", ")
                    : ""
                }
                onChange={handleMultiChange}
                placeholder="value1, value2"
                className="h-9"
                aria-describedby="filter-input-multi-hint"
                data-testid="filter-input-value-multi"
              />
            ) : null}
          </div>
        ) : null}

        <div className="flex items-end">
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={applyDisabled}
            data-testid="filter-input-submit"
          >
            {applyLabel ?? "Apply"}
          </Button>
        </div>
      </div>
      {value && activeOperator?.valueShape === "multi" ? (
        <p
          id="filter-input-multi-hint"
          className="text-xs text-fg-muted"
        >
          Separate multiple values with commas.
        </p>
      ) : null}
    </div>
  );
}
